import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { 
  AppBar, Box, CssBaseline, Divider, Drawer, IconButton, 
  List, ListItem, ListItemButton, ListItemIcon, ListItemText, 
  Toolbar, Typography, Button, Container
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  DirectionsBus as VehicleIcon,
  Person as DriverIcon,
  Route as RouteIcon,
  CloudUpload as ImportIcon,
  CalendarMonth as CalendarIcon,
  Logout as LogoutIcon,
  DirectionsBusFilledOutlined as TripsIcon,
  People as PeopleIcon,
  Place as StopsIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 240;

function MainLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout, isAdmin, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  
  // Ha nincs bejelentkezve a felhasználó, átirányítás a login oldalra
  if (!user && !isLoginPage) {
    return <Navigate to="/login" replace />;
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Menüpontok jogosultság alapján
  const allMenuItems = [
    { text: 'Kezdőlap', icon: <DashboardIcon />, path: '/', permission: null },
    { text: 'Járművek', icon: <VehicleIcon />, path: '/vehicles', permission: 'canManageVehicles' },
    { text: 'Sofőrök', icon: <DriverIcon />, path: '/drivers', permission: 'canManageDrivers' },
    { text: 'Vonalak', icon: <RouteIcon />, path: '/routes', permission: 'canManageRoutes' },
    { text: 'Járatok', icon: <TripsIcon />, path: '/trips', permission: 'canManageTrips' },
    { text: 'Megállók', icon: <StopsIcon />, path: '/stops', permission: 'canManageRoutes' },
    { text: 'Menetrend naptár', icon: <CalendarIcon />, path: '/schedule-xml', permission: 'canAssignTemplates' },
    { text: 'GTFS importálás', icon: <ImportIcon />, path: '/import', permission: 'adminOnly' },
    { text: 'Adatbázis mentés', icon: <StorageIcon />, path: '/database-backup', permission: 'adminOnly' },
    { text: 'Felhasználók', icon: <PeopleIcon />, path: '/users', permission: 'adminOnly' }
  ];

  // Szűrjük a menüpontokat jogosultság alapján
  const menuItems = allMenuItems.filter(item => {
    if (item.permission === null) return true; // Mindenki láthatja
    if (item.permission === 'adminOnly') return isAdmin(); // Csak admin
    return hasPermission(item.permission); // Jogosultság ellenőrzés
  });

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          T-Busz Adatbázis
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton onClick={() => navigate(item.path)}>
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Kijelentkezés" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  if (isLoginPage) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <CssBaseline />
        <Outlet />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            T-Busz Adatbázis
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
        <Container maxWidth="lg">
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
}

export default MainLayout;
